import React, { memo, useCallback, useState, useRef } from "react";
import PropTypes from "prop-types";
import { InputBase, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import clsx from "clsx";

import BodyCell from "./BodyCell";
import palette from "../../styles/palette";
import { handleTooltipVisibility } from "../../../models/UI";

const useStyles = makeStyles(({ typography }) => {
  const { pxToRem } = typography;

  return {
    input: {
      fontSize: pxToRem(13),
      padding: `${pxToRem(6)} ${pxToRem(4)} ${pxToRem(4)}`,
      border: `1px solid ${palette.typography.black.solid}`,
      color: palette.blue,
      background: palette.typography.white.solid,
    },
    invalid: {
      borderColor: palette.dpdRed,
      color: palette.dpdRed,
    },
    tooltip: {
      background: palette.grey,
      boxShadow: `0px 1px 5px rgba(0, 0, 0, 0.2), 0px 4px 4px rgba(0, 0, 0, 0.25), 0px 4px 4px rgba(0, 0, 0, 0.25)`,
      borderRadius: 2,
      fontWeight: "normal",
      padding: `${pxToRem(2)} ${pxToRem(5)}`,
    },
  };
});

const Input = ({ error, ...props }) => {
  const classes = useStyles();

  const [tooltipPosition, setTooltipPosition] = useState(null);

  const hideTooltip = useCallback(
    handleTooltipVisibility(tooltipPosition, setTooltipPosition),
    [tooltipPosition]
  );

  return (
    <Tooltip
      title={error}
      open={!!error}
      classes={{
        tooltip: classes.tooltip,
      }}
      PopperProps={{
        placement: "right-start",
        popperOptions: {
          modifiers: {
            offset: {
              enabled: true,
              offset: "-10, -25",
            },
            flip: {
              enabled: false,
            },
            hide: {
              enable: true,
              fn: hideTooltip,
            },
          },
        },
      }}
    >
      <InputBase {...props} />
    </Tooltip>
  );
};

const EditableCell = memo(({ onSubmit, value, rowData, ...props }) => {
  const classes = useStyles();
  const [isEditable, setEditable] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [fieldError, setFieldError] = useState("");
  const formRef = useRef();

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      onSubmit(
        formRef.current.input.value,
        {
          setFieldError,
          resetForm: () => {
            setEditable(false);
            setInputValue(value);
            setFieldError("");
          },
        },
        rowData
      );
    },
    [onSubmit, rowData, value]
  );

  const handleInputChange = useCallback((e) => {
    setInputValue(e.target.value);
    setFieldError("");
  }, []);

  const editValue = useCallback((e) => {
    e.stopPropagation();
    setEditable(true);
  }, []);

  const handleInputClick = useCallback((e) => e.stopPropagation(), []);
  const handleInputBlur = useCallback(() => {
    setEditable(false);
    setInputValue(value);
    setFieldError("");
  }, [value]);

  if (isEditable) {
    return (
      <form ref={formRef} onSubmit={handleSubmit}>
        <Input
          value={inputValue}
          name="input"
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onClick={handleInputClick}
          autoFocus
          error={fieldError}
          classes={{
            input: clsx(classes.input, {
              [classes.invalid]: !!fieldError,
            }),
          }}
        />
      </form>
    );
  }

  return <BodyCell {...props} value={value} onClick={editValue} />;
});

EditableCell.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
  rowData: PropTypes.object.isRequired,
};

export default EditableCell;
